import React from 'react';
import { formatNumber } from 'humanize-plus';
import { FormProvider, useForm, useWatch } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import moment from 'moment';

import Button, { BUTTON_TYPES } from '@buyside/components/presentation/Button/button';
import CheckboxField from '@buyside/components/presentation/Fields/checkboxField';
import IdeaUpdateAttachments from '@buyside/components/presentation/IdeaShowPage/AuthorActions/IdeaUpdateSections/ideaUpdateAttachments';
import IdeaUpdateDetails from '@buyside/components/presentation/IdeaShowPage/AuthorActions/IdeaUpdateSections/ideaUpdateDetails';
import IdeaUpdateImpact from '@buyside/components/presentation/IdeaShowPage/AuthorActions/IdeaUpdateSections/ideaUpdateImpact';
import IdeaUpdatePositions from '@buyside/components/presentation/IdeaShowPage/AuthorActions/IdeaUpdateSections/ideaUpdatePositions';

import {
  determineIsSubunitCurrency,
  determineParentCurrencyCode
} from '@buyside/util/researchSubmission';
import { formatAttachmentsArray } from '@buyside/util/ideaUpdateFormHelper';
import { ideaUpdateFormValidationSchema } from '@buyside/util/ideaUpdateFormValidation';

import {
  CreateOrEditIdeaUpdateMutationVariables,
  IdeaUpdatesQuery,
  IdeaAttachment,
  ResearchAttachmentAttributes,
  Maybe,
  useCreateOrEditIdeaUpdateMutation
} from '@interfaces/graphql-typings';
export type IdeaUpdateFormValues = Omit<CreateOrEditIdeaUpdateMutationVariables, 'attachments'> & {
  attachments: ResearchAttachmentAttributes[];
};

import styles from './authorActionsStyles.scss';

const IdeaUpdateForm: React.FC<{
  attachments: IdeaAttachment[];
  body: string;
  catalysts: string[];
  closeIdea: boolean;
  closeModal: () => void;
  createdAt: string;
  currencyCode: Maybe<string>;
  direction: string;
  ideaId: string;
  ideaUpdateId?: string;
  impact: string;
  isNewUpdate: boolean;
  isPairTrade: boolean;
  latestPrice: Maybe<number>;
  openingPrice: Maybe<number>;
  priceTarget?: Maybe<string>;
  returnTarget?: Maybe<string>;
  situation: string;
  targetAllocation: string;
  timeframe: string;
}> = ({
  attachments,
  body,
  catalysts,
  closeIdea,
  closeModal,
  createdAt,
  currencyCode,
  direction,
  ideaId,
  ideaUpdateId,
  impact,
  isNewUpdate,
  isPairTrade,
  latestPrice,
  openingPrice,
  priceTarget,
  returnTarget,
  situation,
  targetAllocation,
  timeframe
}) => {
  const date = ideaUpdateId
    ? moment(new Date(createdAt)).format('M/D/YY')
    : moment().format('M/D/YY');

  const [createOrEditIdeaUpdate, { loading, error }] = useCreateOrEditIdeaUpdateMutation({
    update(cache, res) {
      const newUpdate = res.data?.createOrEditIdeaUpdate?.ideaUpdate;
      if (!newUpdate) return;

      cache.modify<IdeaUpdatesQuery['idea']>({
        id: cache.identify({ id: ideaId, __typename: 'Idea' }),
        fields: {
          ideaUpdates: (prevUpdates, { readField }) => {
            if (!prevUpdates) return [newUpdate];

            const editedUpdate = prevUpdates.some(
              update => update && readField('id', update) === newUpdate.id
            );
            return editedUpdate
              ? prevUpdates.map(update =>
                  update && readField('id', update) === newUpdate.id ? newUpdate : update
                )
              : [newUpdate, ...prevUpdates];
          }
        }
      });
    }
  });

  const methods = useForm<IdeaUpdateFormValues>({
    resolver: yupResolver(ideaUpdateFormValidationSchema(openingPrice, direction, isPairTrade)),
    defaultValues: {
      attachments: formatAttachmentsArray(attachments),
      body,
      catalysts,
      closeIdea,
      ideaId,
      ideaUpdateId,
      impact,
      priceTarget,
      returnTarget,
      situation,
      targetAllocation,
      timeframe
    }
  });

  const {
    formState: { isSubmitting, isDirty },
    handleSubmit,
    reset
  } = methods;

  const onSubmit = handleSubmit(values =>
    createOrEditIdeaUpdate({ variables: values }).then((response): void => {
      if (response.data?.createOrEditIdeaUpdate?.ideaUpdate) {
        const {
          body,
          catalystNames,
          closed,
          ideaAttachmentsArray,
          impact,
          priceTarget,
          returnTarget,
          situation,
          targetAllocation,
          timeframe
        } = response.data.createOrEditIdeaUpdate.ideaUpdate;

        reset({
          attachments: isNewUpdate
            ? []
            : formatAttachmentsArray(ideaAttachmentsArray as IdeaAttachment[]),
          body: isNewUpdate ? null : body,
          catalysts: catalystNames || [],
          closeIdea: closed,
          ideaId,
          ideaUpdateId: isNewUpdate ? null : ideaUpdateId,
          impact: isNewUpdate ? null : impact,
          priceTarget:
            Boolean(priceTarget) || priceTarget === 0 ? formatNumber(priceTarget || 0, 2) : null,
          returnTarget: returnTarget ? formatNumber(returnTarget, 2) : null,
          situation,
          targetAllocation,
          timeframe
        });
        closeModal();
      }
    })
  );

  const isClosingIdea = useWatch({ control: methods.control, name: 'closeIdea' });

  return (
    <FormProvider {...methods}>
      <form id="ideaUpdateForm" onSubmit={onSubmit}>
        <div className={styles.sectionContainer}>
          <div className={styles.closeIdea}>
            <CheckboxField name="closeIdea" label="Close this position after saving this update" />
            {isClosingIdea && latestPrice ? (
              <p className={styles.closingPriceMessage}>
                <strong>Notice:</strong> Your idea will close at{' '}
                <strong>
                  {`${latestPrice.toFixed(2)} ${determineParentCurrencyCode(currencyCode || '')}`}
                  {determineIsSubunitCurrency(currencyCode || '')
                    ? ` (${latestPrice * 100} ${currencyCode})`
                    : null}
                </strong>
                . If you would like to utilize a more recent market price, please close the idea
                tomorrow.
              </p>
            ) : null}
          </div>
        </div>
        <IdeaUpdateDetails date={date} />
        <IdeaUpdateAttachments />
        {!isClosingIdea && (
          <>
            <IdeaUpdatePositions
              currencyCode={currencyCode}
              createdAt={createdAt}
              isPairTrade={isPairTrade}
            />
            <IdeaUpdateImpact />
          </>
        )}
        {error && <div className={styles.errorText}>{error.message}</div>}
        <Button
          disabled={!isClosingIdea && !isDirty}
          isLoading={isSubmitting || loading}
          text="Save Update"
          type={BUTTON_TYPES.PRIMARY}
        />
        <Button
          buttonType={BUTTON_TYPES.BUTTON}
          disabled={isSubmitting || loading}
          onClick={(): void => {
            reset();
            closeModal();
          }}
          text="Cancel"
        />
      </form>
    </FormProvider>
  );
};

export default IdeaUpdateForm;
